/**
 * bbShownPayload — pure reconstruction of the legacy `bbShown` GA payload.
 *
 * Why this module exists:
 *   The `bbShown` event was originally assembled server-side using legacy field
 *   names (`url`/`id`/`target`) that differ from the current Bridge Bar API
 *   model (`uri`/`solutionId`/`applicationId`/`mode`). Historical GA dashboards
 *   depend on the legacy field names — this module translates the new model back
 *   to the legacy format without touching the API or component code (F-9, AD-6).
 *
 * Why `solutionSwitcherData` is null (NOT `{}` or `[]`) when absent:
 *   Analytics dashboards distinguish "no solution data was available" from
 *   "solution data was available but empty". Sending `{}` would look like a
 *   partially loaded payload rather than a signal that the feature is off (F-9).
 *
 * Why `error` is null on success:
 *   Dashboards aggregate on `error === null` to compute success rates. A missing
 *   field would be treated as unknown, inflating the "not measured" bucket.
 *
 * TODO(OQ-1): Confirm with analytics whether the full reconstruction depth
 *   (bridgeBarState, messages, extraUserLinks) is required for initial rollout,
 *   or whether a subset is sufficient for Phase 0 dashboards.
 */

import type {BridgeBarConfig} from "../config/BridgeBarConfig";
import type {
  Application,
  BridgeUser,
  SolutionLink,
  UserLink
} from "../backend/BridgeBarData";

// ── Legacy-shape types (GA contract — do not rename without dashboard migration) ──

/** Legacy shape for a leaf (non-composed) solution link. */
interface LegacyLeafSolution {
  id: string;
  label: string;
  url: string;
  target: string;
}

/** Legacy shape for a composed solution group with sub-applications. */
interface LegacyComposedSolution {
  id: string;
  label: string;
  applications: LegacyApplication[];
}

/** Legacy shape for a sub-application within a composed solution group. */
interface LegacyApplication {
  id: string;
  label: string;
  url: string;
  target: string;
}

type LegacySolution = LegacyLeafSolution | LegacyComposedSolution;

/** Top-level solutionSwitcherData shape sent in the bbShown payload. */
interface SolutionSwitcherData {
  currentId: string;
  solutionLinks: LegacySolution[];
}

/** Top-level bbShown payload shape. */
export interface BbShownPayload {
  platformId: string | null;
  solutionSwitcherData: SolutionSwitcherData | null;
  userMenuData: UserMenuData | null;
  error: string | null;
}

/** Legacy user-menu data shape. */
interface UserMenuData {
  userLinks: LegacyUserLink[];
}

/** Legacy user link shape (mirrors UserLink but uses legacy field names). */
interface LegacyUserLink {
  label: string;
  url: string;
  target: string;
  menu: string;
}

// ── Internal translators ────────────────────────────────────────────────────

/**
 * Translate a new-model Application to its legacy GA shape.
 * Field mapping: applicationId→id, uri→url, mode→target.
 */
function translateApplication(app: Application): LegacyApplication {
  return {
    id: app.applicationId,
    label: app.label,
    url: app.uri,
    target: app.mode
  };
}

/**
 * Translate a SolutionLink to its legacy GA shape.
 *
 * A solution is composed when it has one or more applications — in that case
 * the legacy payload carries `applications[]` rather than `url`/`target` on the
 * group itself. Leaf solutions carry `url`/`target` directly.
 *
 * Field mapping: solutionId→id, uri→url, mode→target.
 */
function translateSolution(link: SolutionLink): LegacySolution {
  if (link.applications && link.applications.length > 0) {
    return {
      id: link.solutionId,
      label: link.label,
      applications: link.applications.map(translateApplication)
    };
  }
  return {
    id: link.solutionId,
    label: link.label,
    url: link.uri,
    target: link.mode
  };
}

/**
 * Translate a UserLink to its legacy GA shape.
 * Field mapping: uri→url, mode→target.
 */
function translateUserLink(link: UserLink): LegacyUserLink {
  return {
    label: link.label,
    url: link.uri,
    target: link.mode,
    menu: link.menu
  };
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Build the `bbShown` event payload from current-model BridgeUser data.
 *
 * `solutionSwitcherData` is `null` (not `{}`) when `solution_links` is absent
 * or empty — dashboards distinguish "no solution data" from empty data (F-9).
 *
 * `currentId` is set to `config.applicationId` per OQ-3 resolution: the app
 * context is the correct source of the current application identity, not the
 * legacy empty-string fallback.
 *
 * `error` is `null` on success; `error.toString()` on failure so the
 * stringified error class and message are both captured (AC-2.3).
 *
 * @param data - the BridgeUser from the API response
 * @param config - the BridgeBarConfig for the host application
 * @param status - the resolved data context status
 * @param error - the error object when status is `"error"`
 */
export function buildBbShownPayload(
  data: BridgeUser,
  config: BridgeBarConfig,
  status: "complete" | "error" | "pending",
  error?: Error
): BbShownPayload {
  // Platform ID from the user's root identifier
  const platformId = data.id ?? null;

  // Error field: null on success; stringified error on failure (AC-2.3)
  const errorField =
    status === "error" ? error?.toString() ?? "Unknown analytics error" : null;

  // solutionSwitcherData: null when no solutions — NOT empty object (F-9)
  const solutionSwitcherData =
    data.solution_links && data.solution_links.length > 0
      ? {
          // OQ-3: use config.applicationId — not the legacy "" fallback
          currentId: config.applicationId,
          solutionLinks: data.solution_links.map(translateSolution)
        }
      : null;

  // userMenuData: reconstruct from user_links
  // TODO(OQ-1): confirm whether bridgeBarState, messages, extraUserLinks are
  // needed for Phase 0 analytics or if userLinks alone is sufficient.
  const userMenuData =
    data.user_links && data.user_links.length > 0
      ? {
          userLinks: data.user_links.map(translateUserLink)
        }
      : null;

  return {
    platformId,
    solutionSwitcherData,
    userMenuData,
    error: errorField
  };
}
